import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from "@mui/icons-material/Add";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DomainOutlinedIcon from "@mui/icons-material/DomainOutlined";
import { addDomain, listDomains, removeDomain } from "~/services/admin/domains";
import { COLORS } from "~/ui/tokens";
import ConfirmDialog from "~/shared/ui/ConfirmDialog";
import { useNotify } from "~/shared/ui/feedback/SnackbarProvider";
import PageHeader from "~/shared/ui/PageHeader";
import { EmptyState, LoadErrorBanner, PersonAvatar } from "../shared/StatePanels";
import { accessErrorMessage, formatDateBR } from "../Access/labels";

export const normalizeDomain = (input) => {
  let value = (input || "").trim().toLowerCase();
  value = value.replace(/^[a-z]+:\/\//, "").replace(/\/.*$/, "");
  if (value.includes("@")) value = value.slice(value.lastIndexOf("@") + 1);
  return value;
};

export const isValidDomain = (domain) =>
  domain.length <= 253 && /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(domain);

// createdBy = { id, name } | null (spec §6.2). null = sem autor humano (bootstrap/comando Ace).
const creatorName = (createdBy) => createdBy?.name || "Sistema";

export default function DomainsPage() {
  const [state, setState] = useState({ status: "loading", rows: [] });
  const [attempt, setAttempt] = useState(0);
  const [input, setInput] = useState("");
  const [inputError, setInputError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [toRemove, setToRemove] = useState(null);
  const notify = useNotify();

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, status: "loading" }));
    listDomains().then((rows) => alive && setState({ status: "ok", rows }), () => alive && setState((s) => ({ ...s, status: "error" })));
    return () => {
      alive = false;
    };
  }, [attempt]);

  const reload = () => setAttempt((n) => n + 1);

  const add = async (event) => {
    event.preventDefault();
    const domain = normalizeDomain(input);
    if (!isValidDomain(domain)) {
      setInputError("Domínio inválido. Use o formato empresa.com.br.");
      return;
    }
    setAdding(true);
    setInputError(null);
    try {
      await addDomain(domain);
      setInput("");
      reload();
    } catch (e) {
      setInputError(accessErrorMessage(e, "Não foi possível adicionar o domínio."));
    } finally {
      setAdding(false);
    }
  };

  // ConfirmDialog cuida do "ocupado", do erro inline e de só fechar no sucesso.
  const confirmRemove = async () => {
    await removeDomain(toRemove.id);
    notify({ severity: "success", message: `Domínio ${toRemove.domain} removido.` });
    reload();
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader title="Domínios do grupo" description='E-mails destes domínios podem ser convidados sem a confirmação de "e-mail externo".' />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 300px" }, gap: 2, alignItems: "start" }}>
        <Paper variant="outlined">
          <Box component="form" onSubmit={add} sx={{ display: "flex", gap: 2, alignItems: "flex-start", p: 2, flexWrap: "wrap" }}>
            <TextField
              size="small"
              label="Novo domínio"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              error={Boolean(inputError)}
              helperText={inputError || " "}
              InputProps={{ startAdornment: <InputAdornment position="start">@</InputAdornment> }}
              sx={{ minWidth: 300, flexGrow: 1 }}
            />
            <LoadingButton type="submit" variant="contained" startIcon={<AddIcon />} loading={adding} sx={{ bgcolor: COLORS.primary }}>Adicionar domínio</LoadingButton>
            {state.status === "ok" && <Typography variant="body2" sx={{ color: COLORS.muted, alignSelf: "center" }}>{`${state.rows.length} ${state.rows.length === 1 ? "domínio" : "domínios"}`}</Typography>}
          </Box>
          {state.status === "error" ? (
            <Box sx={{ px: 2 }}><LoadErrorBanner message="Não foi possível carregar os domínios. Tente novamente em instantes." onRetry={reload} /></Box>
          ) : state.status === "ok" && state.rows.length === 0 ? (
            <EmptyState icon={DomainOutlinedIcon} title="Nenhum domínio na lista" text="Sem domínios, todo convite pede a confirmação de e-mail externo." />
          ) : (
            <Table size="small" aria-label="Domínios do grupo">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Domínio</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Adicionado por</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Adicionado em</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {state.status === "loading" && state.rows.length === 0
                  ? Array.from({ length: 5 }).map((_, i) => <TableRow key={i}>{Array.from({ length: 4 }).map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)
                  : state.rows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, fontWeight: 600 }}>
                            <AlternateEmailIcon sx={{ fontSize: 18, color: COLORS.muted }} aria-hidden />
                            {row.domain}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <PersonAvatar name={creatorName(row.createdBy)} size={24} />
                            {creatorName(row.createdBy)}
                          </Box>
                        </TableCell>
                        <TableCell>{formatDateBR(row.createdAt)}</TableCell>
                        <TableCell align="right">
                          <IconButton aria-label={`Remover ${row.domain}`} onClick={() => setToRemove(row)}><DeleteOutlineIcon /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          )}
        </Paper>
        <Alert severity="info" icon={false} sx={{ bgcolor: "#e0f5fb" }}>
          Domínio na lista <b>não dá acesso sozinho</b>: toda pessoa ainda precisa de convite. A lista só evita o aviso de "externo".
        </Alert>
      </Box>
      <ConfirmDialog
        open={Boolean(toRemove)}
        onClose={() => setToRemove(null)}
        destructive
        title="Remover domínio?"
        context={toRemove?.domain}
        message={
          <>
            <Typography sx={{ mb: 1 }}>
              Remover <b>{toRemove?.domain}</b> da lista do grupo. Novos convites para esse domínio passam a pedir confirmação de e-mail externo.
            </Typography>
            <Typography>
              Pessoas que já têm acesso <b>não perdem</b> o acesso.
            </Typography>
          </>
        }
        confirmLabel="Remover"
        cancelLabel="Cancelar"
        onConfirm={confirmRemove}
        errorMessage={(e) => accessErrorMessage(e, "Não foi possível remover o domínio.")}
      />
    </Box>
  );
}
